/**
 * useVault — Vault domain hook returning an `AsyncResource<VaultData>`.
 *
 * Mock-data implementation (Requirement 16.1, 16.2). The hook returns
 * the `MOCK_VAULT_DATA` constant synchronously on first render;
 * `loading` is always `false` and `error` is always `null`. `refetch`
 * is a `useCallback`-stable function that bumps internal state so
 * consumers re-render — the data shape and contents do not change.
 *
 * No backend API is called in this spec (Requirement 16.4).
 *
 * The intended GraphQL operations are catalogued in
 * `frontend/src/graphql/meridian/vault.graphql.ts` and referenced
 * below in `// TODO(meridian-real-data):` comments so a future spec
 * can wire them via `generateClient().graphql({ query, variables })`
 * without changing this hook's call sites. `ExpenseSubmissionForm`'s
 * in-memory append behavior (Requirement 12.5) is independent of this
 * hook and stays mock-only in this spec.
 *
 * Future real-data shape (commented out — do not uncomment in this spec):
 *
 *   import { generateClient } from 'aws-amplify/api';
 *   import {
 *     LIST_EXPENSES,
 *     LIST_SCHOLARSHIPS,
 *     LIST_ENDOWMENTS,
 *     SUBMIT_EXPENSE,
 *     APPROVE_EXPENSE,
 *     REJECT_EXPENSE,
 *   } from '@/graphql/meridian/vault.graphql';
 *
 *   const client = generateClient();
 *   // TODO(meridian-real-data):
 *   //   const expenses     = await client.graphql({ query: LIST_EXPENSES });
 *   //   const scholarships = await client.graphql({ query: LIST_SCHOLARSHIPS });
 *   //   const endowments   = await client.graphql({ query: LIST_ENDOWMENTS });
 *   //   await client.graphql({ query: SUBMIT_EXPENSE,  variables: { input } });
 *   //   await client.graphql({ query: APPROVE_EXPENSE, variables: { id } });
 *   //   await client.graphql({ query: REJECT_EXPENSE,  variables: { id, reason } });
 *
 * Spec: meridian-redesign — task 9.4
 * Design: §Hook Contracts; §GraphQL Operation Stubs
 * Validates: Requirements 16.1, 16.2, 16.3, 16.5
 */

import { useCallback, useState } from 'react';
import { MOCK_VAULT_DATA, type VaultData } from '@/types/vault';
import type { AsyncResource } from './asyncResource';

export function useVault(): AsyncResource<VaultData> {
  // The tick is incremented by `refetch` to force a re-render. The mock
  // data shape and contents are invariant under refetch: a future
  // real-data implementation will replace this bump with an actual
  // GraphQL re-issue.
  const [, setTick] = useState(0);

  const refetch = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  return {
    data: MOCK_VAULT_DATA,
    loading: false,
    error: null,
    refetch,
  };
}
